class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    this.input.form.querySelector('.cv-search-scope-select')?.addEventListener('change', () => {
      this.abortController.abort();
      this.removeAttribute('results');
      this.predictiveSearchResults.innerHTML = '';
      this.close();
      this.searchTerm = '';
      this.onChange();
    });
  }

  getScope() {
    return this.input.form.querySelector('.cv-search-scope-select')?.value || 'all';
  }

  getQuery() {
    const value = this.input.value;
    let query = value.replace(/\btitle\s*:\s*/gi, '');

    if (/\btitle\s*:/i.test(value)) {
      query = query.replace(/\bAND\b/g, ' ');
    }

    return query.trim().replace(/\s+/g, ' ');
  }

  onChange() {
    super.onChange();
    this.removeAttribute('results');
    this.close();
    this.predictiveSearchResults.innerHTML = '';
    this.searchTerm = this.getQuery();

    if (!this.searchTerm) {
      this.abortController.abort();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery() || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);

    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.close(true);
    }
  }

  onFocus() {
    const query = this.getQuery();

    if (!query) return;

    if (query !== this.searchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(query);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (event.target !== this.input) return;

    if (!this.getQuery()) {
      this.abortController.abort();
      this.close(true);
      return;
    }

    if (event.code === 'ArrowUp') {
      event.preventDefault();
      this.switchOption('up');
    } else if (event.code === 'ArrowDown') {
      event.preventDefault();
      this.switchOption('down');
    } else if (event.code === 'Enter') {
      this.selectOption();
    } else if (event.code === 'Escape') {
      this.abortController.abort();
      this.close();
    }
  }

  onKeydown(event) {
    if (
      event.target === this.input &&
      ['ArrowUp', 'ArrowDown'].includes(event.code)
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.hasAttribute('open')) return;

    const options = Array.from(
      this.querySelectorAll('li[role="option"], button[role="option"]')
    ).filter((element) => element.offsetParent !== null);

    if (!options.length) return;

    const selected = this.querySelector('[aria-selected="true"]');
    const index = options.indexOf(selected);

    if (direction === 'up' && index === -1) return;

    const next = direction === 'up'
      ? (index - 1 + options.length) % options.length
      : (index + 1) % options.length;

    selected?.setAttribute('aria-selected', 'false');
    options[next].setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', options[next].id);

    if (this.statusElement) this.statusElement.textContent = '';
  }

  selectOption() {
    this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    )?.click();
  }

  async getSearchResults(searchTerm) {
    const scope = this.getScope();
    const queryKey = JSON.stringify([scope, searchTerm]);

    this.abortController.abort();
    this.abortController = new AbortController();

    const signal = this.abortController.signal;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const titleMode = scope === 'title';
    const sectionId = titleMode ? 'cv-title-search' : 'predictive-search';
    const endpoint = titleMode
      ? this.input.form.action
      : routes.predictive_search_url;

    const url = new URL(endpoint, window.location.origin);

    const query = titleMode
      ? searchTerm.split(' ').map((word) => {
          return 'title:' + word.replace(/([\\:()"])/g, '\\$1');
        }).join(' ')
      : searchTerm;

    url.searchParams.set('q', query);
    url.searchParams.set('section_id', sectionId);

    if (titleMode) {
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
    } else {
      url.searchParams.set(
        'resources[options][fields]',
        'title,body,tag,author,product_type,vendor,variants.title,variants.sku,variants.barcode'
      );
    }

    try {
      const response = await fetch(url.toString(), { signal });

      if (!response.ok) {
        throw new Error('Search request failed: ' + response.status);
      }

      const text = await response.text();

      if (
        signal.aborted ||
        this.getQuery() !== searchTerm ||
        this.getScope() !== scope
      ) {
        return;
      }

      const section = new DOMParser()
        .parseFromString(text, 'text/html')
        .querySelector('#shopify-section-' + sectionId);

      if (!section) {
        throw new Error('Missing search section: ' + sectionId);
      }

      const markup = section.innerHTML;

      if (Object.keys(this.cachedResults).length >= 30) {
        this.cachedResults = {};
      }

      this.cachedResults[queryKey] = markup;
      this.removeAttribute('loading');

      if (this.contains(document.activeElement)) {
        this.renderSearchResults(markup);
      }
    } catch (error) {
      if (signal.aborted || error.name === 'AbortError') return;

      this.close();
      console.error('Search suggestions:', error);
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement ||
      this.querySelector('.predictive-search-status');

    this.setLiveRegionText(this.getAttribute('data-loading-text') || '');
    this.setAttribute('loading', 'true');
  }

  setLiveRegionText(text) {
    this.statusElement = this.statusElement ||
      this.querySelector('.predictive-search-status');

    if (!this.statusElement) return;

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = text;

    clearTimeout(this.statusTimer);

    this.statusTimer = setTimeout(() => {
      this.statusElement?.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(markup) {
    this.predictiveSearchResults.innerHTML = markup;

    const suffix = this.input.id || 'search';

    this.predictiveSearchResults
      .querySelectorAll('[role="option"][id]')
      .forEach((option) => {
        option.id = option.id + '-' + suffix;
      });

    this.setAttribute('results', 'true');
    this.removeAttribute('loading');

    this.setLiveRegionText(
      this.querySelector(
        '[data-predictive-search-live-region-count-value]'
      )?.textContent || ''
    );

    this.open();
  }

  open() {
    const header = document.querySelector('.section-header');
    const bottom = header
      ? header.getBoundingClientRect().bottom
      : this.getBoundingClientRect().bottom;

    this.predictiveSearchResults.style.maxHeight =
      Math.max(120, window.innerHeight - bottom) + 'px';

    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    this.querySelector('[aria-selected="true"]')
      ?.setAttribute('aria-selected', 'false');

    this.input.removeAttribute('aria-activedescendant');
    this.input.setAttribute('aria-expanded', 'false');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.predictiveSearchResults.removeAttribute('style');
  }

  disconnectedCallback() {
    this.abortController.abort();
    clearTimeout(this.statusTimer);
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}